import Vue from 'vue'
import App from './App.vue'
import vuetify from './plugins/vuetify'
import router from './router'
import store from './store'
import axios from 'axios'
import { url } from './client_config/config'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import * as rules from 'vee-validate/dist/rules'
import { messages } from 'vee-validate/dist/locale/en.json'

// Import Vuetify styles
import 'vuetify/dist/vuetify.min.css'
import './styles/app-filter-controls.css'
import './styles/brand-tokens.css'
import './styles/form-section-surfaces.css'
import './styles/app-buttons.css'
import './styles/app-elevated-surfaces.css'
import './styles/production-surfaces.css'
import './styles/data-table-surface.css'
import './styles/bulk-import-preview.css'
import './styles/app-table-options-menu.css'
import './styles/dashboard-feed.css'
import './styles/app-stat-card.css'
import './styles/audition-stat-card.css'
import './styles/app-page-shell.css'
import AppHeaderIconButton from '@/components/common/AppHeaderIconButton.vue'
import AppHeaderBackButton from '@/components/common/AppHeaderBackButton.vue'
import FormSectionCard from '@/components/common/FormSectionCard.vue'

Vue.component('AppHeaderIconButton', AppHeaderIconButton)
Vue.component('AppHeaderBackButton', AppHeaderBackButton)
Vue.component('FormSectionCard', FormSectionCard)

// Pre-compile regex for efficiency (runs on every request)
const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

/**
 * Creates an auth interceptor that attaches tokens to appropriate requests
 * Only attaches auth to relative URLs or URLs that match our API base URL
 * This prevents tokens from being sent to third-party APIs
 */
const createAuthInterceptor = (baseURL) => (config) => {
  const targetUrl = config?.url || '';

  // Skip auth for absolute URLs that aren't our API (e.g., Zoom, Google Meet)
  const isAbsoluteUrl = ABSOLUTE_URL_PATTERN.test(targetUrl);
  const shouldAttachAuth = !isAbsoluteUrl || targetUrl.startsWith(baseURL);

  if (shouldAttachAuth) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const viewAsCompanyId = store.getters['auth/viewAsCompanyId'];
    if (viewAsCompanyId) {
      config.headers['X-View-As-Company'] = viewAsCompanyId;

      // Ensure query param for routes that only read companyId from query/body
      config.params = config.params || {};
      if (config.params.companyId == null && config.params.company == null) {
        config.params.companyId = viewAsCompanyId;
      }

      const method = (config.method || 'get').toLowerCase();
      if (['post', 'put', 'patch'].includes(method)) {
        const body = config.data;
        if (body && typeof body === 'object' && !(body instanceof FormData) && !(body instanceof URLSearchParams)) {
          if (body.companyId == null && body.company == null) {
            config.data = { ...body, companyId: viewAsCompanyId };
          }
        }
      }
    }
  }

  return config;
};

const authErrorHandler = (error) => Promise.reject(error);

// Create axios instance
const http = axios.create({
  baseURL: url,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include auth token
http.interceptors.request.use(
  createAuthInterceptor(url),
  authErrorHandler
);

// Set up global axios instance with same auth logic
axios.interceptors.request.use(
  createAuthInterceptor(url),
  authErrorHandler
);

// Add response interceptor to global axios instance for auth errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, clear auth and redirect to login
      store.dispatch('auth/logout');
      if (router.currentRoute.path !== '/login') {
        router.push('/login');
      }
    } else if (error.response?.status === 0 || error.code === 'NETWORK_ERROR') {
      // Network error
      store.dispatch('showError', 'Network error. Please check your connection and try again.');
    }

    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
http.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      store.dispatch('auth/logout');
      if (router.currentRoute.path !== '/login') {
        router.push('/login');
      }
    } else if (error.response?.status === 0 || error.code === 'NETWORK_ERROR') {
      // Network error
      store.dispatch('showError', 'Network error. Please check your connection and try again.');
    }

    return Promise.reject(error);
  }
);

// Set axios as global property
Vue.prototype.$http = http;

// Set up vee-validate
Object.keys(rules).forEach(rule => {
  extend(rule, {
    ...rules[rule],
    message: messages[rule]
  });
});

// Custom validation rules
extend('alpha_spaces', {
  validate: value => {
    return /^[a-zA-Z\s'-]+$/.test(value);
  },
  message: 'This field can only contain letters, spaces, hyphens, and apostrophes'
});

extend('password_strength', {
  validate: value => {
    const hasLower = /(?=.*[a-z])/.test(value);
    const hasUpper = /(?=.*[A-Z])/.test(value);
    const hasNumber = /(?=.*\d)/.test(value);
    const hasSpecial = /(?=.*[@$!%*?&])/.test(value);
    return hasLower && hasUpper && hasNumber && hasSpecial;
  },
  message: 'Password must contain at least one lowercase letter, uppercase letter, number, and special character (@$!%*?&)'
});

extend('phone', {
  validate: value => {
    const cleaned = value.replace(/\s/g, '');
    return /^[+]?[1-9][\d]{7,15}$/.test(cleaned);
  },
  message: 'Please enter a valid phone number (8-15 digits)'
});

extend('headshot_file', {
  validate: value => {
    if (!value) return false;
    if (value.size > 5 * 1024 * 1024) return false;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    return validTypes.includes(value.type);
  },
  message: 'Please upload a valid image file (JPG, PNG, GIF) under 5MB'
});

extend('resume_file', {
  validate: value => {
    if (!value) return false;
    if (value.size > 10 * 1024 * 1024) return false;
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    return validTypes.includes(value.type);
  },
  message: 'Please upload a valid document file (PDF, DOC, DOCX) under 10MB'
});

// Register vee-validate components globally
Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);

// Add global $confirm method
Vue.prototype.$confirm = function (message, title) {
  return this.$store.dispatch('showConfirmation', { message, title });
};

Vue.config.productionTip = false;

// Ignore ResizeObserver errors in Vue's error handler (benign layout quirk after logout/navigation)
const defaultErrorHandler = Vue.config.errorHandler;
Vue.config.errorHandler = (err, vm, info) => {
  const msg = err?.message || String(err);
  if (msg.includes('ResizeObserver loop') || msg.includes('ResizeObserver loop limit')) {
    return;
  }
  console.error('Global Vue error:', {
    error: err,
    component: vm?.$options?.name || 'Unknown',
    info
  });
  store.dispatch('showError', 'Something went wrong. Please refresh the page.');
  if (defaultErrorHandler) {
    defaultErrorHandler(err, vm, info);
  } else {
    // Re-throw so the error is still reported (e.g. console, devtools)
    throw err;
  }
};

// Suppress ResizeObserver loop error (benign browser quirk during layout changes, e.g. after logout)
// See: https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
window.addEventListener('error', (event) => {
  if (
    event.message === 'ResizeObserver loop completed with undelivered notifications.' ||
    (event.message && event.message.includes('ResizeObserver loop'))
  ) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return true;
  }
}, true);

// Handle unhandled promise rejections from router navigation and benign errors
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  const msg = error?.message || String(error);
  // Suppress ResizeObserver loop (benign layout quirk)
  if (msg.includes('ResizeObserver loop')) {
    event.preventDefault();
    return;
  }
  // Suppress expected navigation errors
  if (
    error &&
    (
      error.name === 'NavigationDuplicated' ||
      error.name === 'NavigationRedirectedError' ||
      error.message?.includes('Avoided redundant navigation') ||
      error.message?.includes('Redirected') ||
      error.message?.includes('via a navigation guard')
    )
  ) {
    event.preventDefault();
    return;
  }
});

new Vue({
  vuetify,
  router,
  store,
  render: h => h(App)
}).$mount('#app') 
